// Edit modal for the fields NewApplication's create form collects that
// actually persist onto the Application row. Layout deliberately mirrors
// NewApplication's send-intake form: a few always-visible identity/
// assignment fields up top, then a collapsible "Fill out more details"
// section reusing the SAME shared field components NewApplication's own
// optional "fill out some of it" prefill section uses (BusinessDetailsFields,
// AddressFields, OwnershipFields, BankingFields, TransactionMixFields,
// PdiFields) so the inputs are pixel- and behavior-identical, not a
// separately-maintained approximation.
//
// SSN / driver's license number / bank account+routing number / tax ID are
// all rendered too (hideSensitive is NOT passed to BusinessDetailsFields/
// OwnershipFields/BankingFields here), but they're write-only: those
// fields are redacted to a masked last-4 at write time (sharedModules.
// redact_merchant_for_storage), so there is never a raw value on file to
// prefill -- every sensitive field always starts blank on open, and blank
// on submit means "admin didn't type a new value," not "clear it." See
// UpdateApplicationInput / UpdateOwnerInput / UpdateBankAccountInput in
// lib/types.ts for the exact wire shape.
//
// Built on the native <dialog> element, same pattern as ConfirmDialog.
// Open/close and the actual save are parent-controlled: the parent owns
// applicationID/actionBusy and refetches after a successful save.

import { useEffect, useRef, useState } from "react";
import { AlertCircle, ChevronDown, ChevronRight, X } from "lucide-react";
import { ErrorMessage } from "./ErrorMessage";
import { gql, GraphQLError } from "../lib/api";
import { UPDATE_APPLICATION_MUTATION } from "../lib/operations";
import { isOtherPosPlatform } from "../lib/posPlatforms";
import { mnspShippingToPdiLabel } from "../lib/mnspPricing";
import { canonicalMonthToBill, selectPricingTier } from "../lib/pricingTiers";
import { PercentageTotalIndicator } from "./PercentageTotalIndicator";
import { SalesCodeSelect } from "./SalesCodeSelect";
import { templateIdForSalesCode } from "../lib/salesCode";
import {
  EMPTY_MNSP_FORM,
  fromServerMnsp,
  MnspEnrollmentSection,
  toWireMnsp,
  type MnspEnrollmentFormState,
} from "./MnspEnrollmentSection";
import {
  AddressFields,
  annualSecurityBundleFeeInvalid,
  BankingFields,
  buildCloverSecurityAndTransarmorInput,
  buildFeeOverridesInput,
  buildFleetOverridesInput,
  buildIcPlusPricingOverridesInput,
  BusinessDetailsFields,
  CloverSecurityAndTransarmorSection,
  EMPTY_ADDRESS,
  EMPTY_BANK,
  EMPTY_BUSINESS,
  EMPTY_CLOVER_SECURITY_AND_TRANSARMOR,
  EMPTY_FEE_OVERRIDES,
  EMPTY_FLEET_OVERRIDES,
  EMPTY_IC_PLUS_PRICING_OVERRIDES,
  EMPTY_MODE,
  EMPTY_OWNER,
  EMPTY_VOLUME,
  FleetPricingAdjustmentSection,
  IS_DEV_API,
  ManualFeeAdjustmentSection,
  OwnershipFields,
  parseAnnualSecurityBundleFee,
  parseAuthFeeOverride,
  patch,
  PdiFields,
  SameAsToggle,
  Section,
  TF,
  TransactionMixFields,
  VolumeFields,
  type AddressForm,
  type BankForm,
  type BusinessForm,
  type CloverSecurityAndTransarmorForm,
  type FeeOverridesForm,
  type FleetOverridesForm,
  type IcPlusPricingOverridesForm,
  type ModeForm,
  type OwnerForm,
  type VolumeDetailsForms,
} from "./application";
import type {
  Address,
  Application,
  ApplicationResult,
  CardConnectEditAction,
  CloverSecurityAndTransarmorInput,
  FeeOverridesInput,
  FleetOverridesInput,
  IcPlusPricingOverridesInput,
  PdiPrefsInput,
  PersistedSensitiveLast4,
  UpdateApplicationInput,
  UpdateMnspEnrollmentInput,
} from "../lib/types";
import { validateEditApplication } from "../lib/applicationValidation";
import { useFieldErrors, type FieldErrorLike } from "../lib/useFieldErrors";

interface Props {
  open: boolean;
  application: Application;
  onClose: () => void;
  // Called after a save that persisted so the parent can refetch + close.
  // `message` carries the server's note verbatim (e.g. "a fresh signature
  // link was emailed"). `kind` is "warning" when the row saved but a
  // best-effort side step failed -- notably generating the signature link
  // -- so the parent renders `message` red instead of green.
  onSaved: (message: string | null, kind?: "success" | "warning") => void;
}

const EMPTY_PDI: PdiPrefsInput = {
  // "" hydrates from the row; the PdiFields select renders it as "Yes" (send).
  sendPdiOrder: "",
  posPlatform: "",
  singleOrMultiSite: "Single",
  gasPumpYn: "No",
  gasPumpType: "",
  broadbandYn: "No",
  managedWifiInterest: "",
  residentialLocation: "No",
  shippingSpeed: "Standard",
  notes: "",
};

function addressFromApi(a?: Address | null): AddressForm {
  return {
    address1: a?.address1 ?? "",
    address2: a?.address2 ?? "",
    city: a?.city ?? "",
    stateCd: a?.stateCd ?? "",
    zip: a?.zip ?? "",
    countryCd: a?.countryCd ?? "US",
  };
}

function addressesEqual(a: AddressForm, b: AddressForm): boolean {
  return (
    a.address1 === b.address1 &&
    a.address2 === b.address2 &&
    a.city === b.city &&
    a.stateCd === b.stateCd &&
    a.zip === b.zip &&
    a.countryCd === b.countryCd
  );
}

// Reuses toWireMnsp's parsing (rental-fee float coercion, registerOther
// only-when-OTHER_MNSP) but drops signatureImage/acceptances -- neither
// is part of UpdateMnspEnrollmentInput, and AppSync rejects unknown input
// fields outright, so sending toWireMnsp's output as-is would 400.
function toUpdateMnspInput(
  form: MnspEnrollmentFormState,
  mnspEnabled: boolean,
  rentalEnabled: boolean,
): UpdateMnspEnrollmentInput {
  const wire = toWireMnsp(form, mnspEnabled, rentalEnabled);
  return {
    serviceType: wire.serviceType ?? undefined,
    registerOther: wire.registerOther ?? undefined,
    terminalMonthlyRentalFee: wire.terminalMonthlyRentalFee ?? undefined,
    // Same "can't blank via this modal" limitation as terminalMonthlyRentalFee
    // above: a blank field maps to undefined (omitted), which leaves any
    // previously-set override untouched rather than clearing it. To revert
    // to the standard fee, retype it (shown as the input's placeholder).
    deploymentFeeOverride: wire.deploymentFeeOverride ?? undefined,
    monthlyFeeOverride: wire.monthlyFeeOverride ?? undefined,
    gatewayName: wire.gatewayName ?? undefined,
    terminalType: wire.terminalType ?? undefined,
    pinPad: wire.pinPad ?? undefined,
    internetGatewayProvided: wire.internetGatewayProvided ?? undefined,
    shippingSpeed: wire.shippingSpeed ?? undefined,
    shippingFeeOverride: wire.shippingFeeOverride ?? undefined,
    shippingFeeOverrideSpeed: wire.shippingFeeOverrideSpeed ?? undefined,
  };
}

// ----- Admin-only pricing override form <-> wire converters -----------------
// Application.feeOverrides/icPlusPricingOverrides/fleetOverrides come back
// shaped like FeeOverridesInput/IcPlusPricingOverridesInput/FleetOverridesInput
// (numbers/strings/booleans); the *Form types used by ManualFeeAdjustment
// Section/FleetPricingAdjustmentSection are all-string, matching every other
// form in this file (numeric inputs stay strings while typing, parsed at
// submit). Field-by-field rather than a generic loop so each conversion is
// easy to eyeball against its counterpart in NewApplication.tsx.

// AWSJSON fields (feeOverrides/icPlusPricingOverrides/fleetOverrides) are
// stored as a JSON-encoded string in DynamoDB. The APPSYNC_JS resolver
// runtime doesn't expose JSON.parse (see getApplication.js), so it passes
// that string straight through an AWSJSON field -- and confirmed via
// runtime inspection, AppSync's AWSJSON serializer wraps an already-
// stringified value in another layer of JSON encoding rather than
// embedding it as a parsed object. What arrives here is therefore
// double-encoded: a JSON string whose content is itself a JSON string of
// the actual object. Loop until the result isn't a string anymore
// (bounded, so a genuinely malformed value can't spin forever) rather
// than assuming exactly one layer.
function parseMaybeJson<T>(
  value: T | string | null | undefined,
): T | null | undefined {
  let current: unknown = value;
  for (let i = 0; i < 5 && typeof current === "string"; i++) {
    try {
      current = JSON.parse(current);
    } catch {
      return null;
    }
  }
  return current as T | null | undefined;
}

function feeOverridesToForm(
  raw: FeeOverridesInput | null | undefined,
): FeeOverridesForm {
  const fo = parseMaybeJson(raw);
  const s = (v: number | undefined | null) => (v != null ? String(v) : "");
  return {
    achBatchFee: s(fo?.achBatchFee),
    addressVerifFee: s(fo?.addressVerifFee),
    annualMembershipFee: s(fo?.annualMembershipFee),
    appFee: s(fo?.appFee),
    chargebackFee: s(fo?.chargebackFee),
    cloverPlatformFee: s(fo?.cloverPlatformFee),
    cloverSecurityFee: s(fo?.cloverSecurityFee),
    ddaRejectFee: s(fo?.ddaRejectFee),
    earlyCancelFee: s(fo?.earlyCancelFee),
    minProcessFee: s(fo?.minProcessFee),
    monthlyEquipmentRentalFee: s(fo?.monthlyEquipmentRentalFee),
    pciAnnualFee: fo?.pciAnnualFee ?? "",
    pciConciergeMonthlyFee: s(fo?.pciConciergeMonthlyFee),
    pciNonComplianceFee: s(fo?.pciNonComplianceFee),
    pciProgramCd: fo?.pciProgramCd ?? "",
    regProdMonthlyFee: s(fo?.regProdMonthlyFee),
    retrievalFee: s(fo?.retrievalFee),
    statementFee: s(fo?.statementFee),
    transactionFee: s(fo?.transactionFee),
    voiceAuthFee: s(fo?.voiceAuthFee),
    wirelessActivationFee: s(fo?.wirelessActivationFee),
    wirelessFee: s(fo?.wirelessFee),
    duesAndAssessmentsFlg:
      fo?.duesAndAssessmentsFlg != null ? String(fo.duesAndAssessmentsFlg) : "",
    passthruInterchgCostsFlg:
      fo?.passthruInterchgCostsFlg != null
        ? String(fo.passthruInterchgCostsFlg)
        : "",
  };
}

function icPlusPricingOverridesToForm(
  raw: IcPlusPricingOverridesInput | null | undefined,
): IcPlusPricingOverridesForm {
  const v = parseMaybeJson(raw);
  const s = (x: number | undefined | null) => (x != null ? String(x) : "");
  return {
    interchangeTypeCode: v?.interchangeTypeCode ?? "",
    amexOptBlueQualDiscountPct: s(v?.amexOptBlueQualDiscountPct),
    discoverQualCreditDiscountPct: s(v?.discoverQualCreditDiscountPct),
    mastercardQualCreditDiscountPct: s(v?.mastercardQualCreditDiscountPct),
    visaQualCreditDiscountPct: s(v?.visaQualCreditDiscountPct),
  };
}

function fleetOverridesToForm(
  raw: FleetOverridesInput | null | undefined,
): FleetOverridesForm {
  const v = parseMaybeJson(raw);
  // ebtFnsNumber is a string (7-digit id); the rest are numbers -- accept both.
  const s = (x: number | string | undefined | null) =>
    x != null ? String(x) : "";
  return {
    voyagerAuthFee: s(v?.voyagerAuthFee),
    voyagerSalesDiscountPct: s(v?.voyagerSalesDiscountPct),
    wexSalesDiscountPct: s(v?.wexSalesDiscountPct),
    wexTransactionFee: s(v?.wexTransactionFee),
    wexChargebackFee: s(v?.wexChargebackFee),
    wexRetrievalFee: s(v?.wexRetrievalFee),
    pinDebitDiscountFee: s(v?.pinDebitDiscountFee),
    pinDebitTransactionFee: s(v?.pinDebitTransactionFee),
    ebtFnsNumber: s(v?.ebtFnsNumber),
    ebtTransactionFee: s(v?.ebtTransactionFee),
  };
}

function cloverSecurityAndTransarmorToForm(
  raw: CloverSecurityAndTransarmorInput | null | undefined,
): CloverSecurityAndTransarmorForm {
  const v = parseMaybeJson(raw);
  const s = (x: number | string | undefined | null) =>
    x != null ? String(x) : "";
  return {
    // Tri-state select ("" = tier default, "true"/"false" = override),
    // same convention as duesAndAssessmentsFlg/passthruInterchgCostsFlg.
    transarmorDataProtectionFlg:
      v?.transarmorDataProtectionFlg != null
        ? String(v.transarmorDataProtectionFlg)
        : "",
    transarmorTokenTypeCd: s(v?.transarmorTokenTypeCd),
    transarmorMultiPayToken: s(v?.transarmorMultiPayToken),
    transarmorMonthlyFee: s(v?.transarmorMonthlyFee),
    cloverSecurityBundleCd: s(v?.cloverSecurityBundleCd),
    cloverSecurityPlusFee: s(v?.cloverSecurityPlusFee),
    pciComplianceServiceFee: s(v?.pciComplianceServiceFee),
    pciComplianceServiceFeeFrequency: s(v?.pciComplianceServiceFeeFrequency),
    // Month-name dropdown -- normalize a legacy stored value ("3" / "march")
    // to the canonical name so it lands on a real option; anything
    // unrecognized stays as-is and the section flags it.
    pciComplianceServiceFeeMonthToBill:
      canonicalMonthToBill(s(v?.pciComplianceServiceFeeMonthToBill)) ??
      s(v?.pciComplianceServiceFeeMonthToBill),
  };
}

// Whether the row already has any admin-only pricing override set. Drives
// whether "Manual fee adjustment" opens expanded by default -- an operator
// editing an application that already has overrides needs to see them
// without knowing to click and expand a collapsed section first.
function hasAnyOverrides(application: Application): boolean {
  return (
    application.authFeeOverride != null ||
    application.annualSecurityBundleFee != null ||
    Object.keys(parseMaybeJson(application.feeOverrides) ?? {}).length > 0 ||
    Object.keys(parseMaybeJson(application.icPlusPricingOverrides) ?? {})
      .length > 0 ||
    Object.keys(parseMaybeJson(application.cloverSecurityAndTransarmor) ?? {})
      .length > 0 ||
    Object.keys(parseMaybeJson(application.fleetOverrides) ?? {}).length > 0
  );
}

// Read-only confirmation that the sensitive leaves an operator types once
// (SSN, tax ID, driver's license, bank account/routing) were actually
// captured and staged (#130). The edit form itself always renders these
// blank -- they're redacted to last-4 at write time -- so without this an
// operator reviewing an intake has no way to tell a value made it through
// the merchant's edits. `source` distinguishes "confirmed in the raw
// staging blob" (RAW) from "only the post-boarding last-4 remains"
// (REDACTED). Null (nothing rendered) for a TECH caller or when no
// sensitive value was ever captured.
const SENSITIVE_LAST4_LABELS: [keyof PersistedSensitiveLast4, string][] = [
  ["taxId", "Tax ID"],
  ["ownerSsn", "Owner SSN"],
  ["ownerDriversLicenseNumber", "Driver's license #"],
  ["depositAccountNumber", "Deposit account #"],
  ["depositRoutingNumber", "Deposit routing #"],
  ["withdrawalAccountNumber", "Withdrawal account #"],
  ["withdrawalRoutingNumber", "Withdrawal routing #"],
];

function PersistedSensitiveLast4Panel({
  value,
}: {
  value?: PersistedSensitiveLast4 | null;
}) {
  if (!value) return null;
  const rows = SENSITIVE_LAST4_LABELS.filter(
    ([key]) => typeof value[key] === "string" && value[key],
  );
  if (rows.length === 0) return null;
  return (
    <div className="panel px-4 py-3">
      <p className="text-sm font-semibold text-white">Sensitive info on file</p>
      <p className="mt-0.5 text-xs text-brand-muted">
        {value.source === "RAW"
          ? "Confirmed staged for boarding — read from the raw submission, not this form."
          : "From the boarded CardConnect record."}{" "}
        These fields render blank under "Adjust details"; leave them blank to
        keep the values shown here.
      </p>
      <dl className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
        {rows.map(([key, label]) => (
          <div key={key} className="flex justify-between gap-2 text-sm">
            <dt className="text-brand-muted">{label}</dt>
            <dd className="font-mono text-brand-text">••••{value[key]}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

type Errors = Record<string, string>;

// validateEditApplication answers with a keyed record (its call sites index
// it directly); useFieldErrors works in declaration order, which is also the
// order the roll-up banner should list problems in.
function errorsToList(errors: Errors): FieldErrorLike[] {
  return Object.entries(errors).map(([field, message]) => ({ field, message }));
}

export function EditApplicationModal({
  open,
  application,
  onClose,
  onSaved,
}: Props) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  // Always-visible identity/assignment fields.
  const [businessName, setBusinessName] = useState("");
  const [merchantEmail, setMerchantEmail] = useState("");
  const [templateID, setTemplateID] = useState("");
  // Operator-selected CardConnect sales code (#64). "" = Auto. Changing it
  // re-pairs templateID below (persisted to our DB); only the sales code is
  // pushed to CardConnect on the merchant update.
  const [salesCode, setSalesCode] = useState("");
  const [assignedToPIN, setAssignedToPIN] = useState("");
  const [assignedTechEmail, setAssignedTechEmail] = useState("");

  // Collapsible "Fill out more details" section -- same state shapes
  // NewApplication uses for its own optional prefill section, so the
  // imported shared components (BusinessDetailsFields, etc.) work
  // completely unmodified.
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [biz, setBiz] = useState<BusinessForm>(EMPTY_BUSINESS);
  const [bizAddr, setBizAddr] = useState<AddressForm>(EMPTY_ADDRESS);
  const [mailAddr, setMailAddr] = useState<AddressForm>(EMPTY_ADDRESS);
  const [mailSame, setMailSame] = useState(true);
  const [vol, setVol] = useState<VolumeDetailsForms>(EMPTY_VOLUME);
  const [owner, setOwner] = useState<OwnerForm>(EMPTY_OWNER);
  const [ownerAddr, setOwnerAddr] = useState<AddressForm>(EMPTY_ADDRESS);
  const [ownerAddrSame, setOwnerAddrSame] = useState(true);
  const [deposit, setDeposit] = useState<BankForm>(EMPTY_BANK);
  const [withdrawal, setWithdrawal] = useState<BankForm>(EMPTY_BANK);
  const [withdrawalSame, setWithdrawalSame] = useState(true);
  const [mode, setMode] = useState<ModeForm>(EMPTY_MODE);
  const [pdiPrefs, setPdiPrefs] = useState<PdiPrefsInput>(EMPTY_PDI);
  const [posOtherSelected, setPosOtherSelected] = useState(false);
  const [gasOtherSelected, setGasOtherSelected] = useState(false);
  // Only rendered/sent when application.mnspEnrollmentRequested and/or
  // terminalRentalRequested -- an application with neither has nothing
  // here to edit.
  const [mnspForm, setMnspForm] =
    useState<MnspEnrollmentFormState>(EMPTY_MNSP_FORM);
  // Nested collapsible, one level deeper than "Fill out more details" --
  // admin-only pricing overrides, mirroring NewApplication's Manual fee
  // adjustment + Fleet pricing adjustment sections.
  const [feeAdjustmentOpen, setFeeAdjustmentOpen] = useState(false);
  const [authFeeOverride, setAuthFeeOverride] = useState("");
  // Annual security bundle fee -- our records only, never boarded. "" = the
  // $199 tier default.
  const [annualSecurityBundleFee, setAnnualSecurityBundleFee] = useState("");
  const [feeOverrides, setFeeOverrides] =
    useState<FeeOverridesForm>(EMPTY_FEE_OVERRIDES);
  const [icPlusPricingOverrides, setIcPlusPricingOverrides] =
    useState<IcPlusPricingOverridesForm>(EMPTY_IC_PLUS_PRICING_OVERRIDES);
  const [cloverSecurityAndTransarmor, setCloverSecurityAndTransarmor] =
    useState<CloverSecurityAndTransarmorForm>(
      EMPTY_CLOVER_SECURITY_AND_TRANSARMOR,
    );
  const [fleetOverrides, setFleetOverrides] = useState<FleetOverridesForm>(
    EMPTY_FLEET_OVERRIDES,
  );
  // Operator-only free-text notes (issue #130). Plain string, unlike the
  // structured override objects above -- never surfaced to the merchant.
  const [internalNotes, setInternalNotes] = useState("");

  // null when idle; otherwise which button is in flight, so each button can
  // show its own busy label and the others disable.
  const [savingAction, setSavingAction] =
    useState<CardConnectEditAction | null>(null);
  const saving = savingAction !== null;
  const [bannerError, setBannerError] = useState<string | null>(null);
  // Which button was last pressed. Save, Submit to CardConnect, and Resend
  // signature validate different things (identity reentry is only required
  // on Submit), so the live rules follow the operator's last intent.
  const [lastAction, setLastAction] = useState<CardConnectEditAction>("NONE");
  const errorBannerRef = useRef<HTMLDivElement | null>(null);

  // Reveal + scroll to errored fields. The modal's "Fill out more details"
  // (detailsOpen) and "Manual fee adjustment" (feeAdjustmentOpen, nested
  // inside details) sections render their contents conditionally, so an error
  // inside a collapsed section would otherwise make Save silently no-op.
  // Open whichever section holds an errored field, then scroll after the
  // re-render has mounted them.
  function revealSectionsFor(keys: string[]) {
    // These fields render above the collapsible "Adjust details" section;
    // everything else (owner, banking, MNSP, pricing overrides) lives under
    // it. An error on one of these must not force the section open.
    const alwaysVisible = new Set([
      "businessName",
      "merchantEmail",
      "templateID",
      "assignedToPIN",
      "assignedTechEmail",
      "salesCode",
    ]);
    if (keys.some((k) => !alwaysVisible.has(k))) setDetailsOpen(true);
    if (
      keys.some(
        (k) =>
          k.startsWith("fee-override") ||
          k.startsWith("clover-security") ||
          k.startsWith("fleet-override"),
      )
    )
      setFeeAdjustmentOpen(true);
  }

  function focusFirstError(keys: string[]) {
    revealSectionsFor(keys);
    // Two rAFs so the just-opened sections have mounted and laid out before
    // we measure. Field ids that match a rendered input's DOM id (the pricing
    // overrides and the MNSP fee inputs) scroll precisely; anything else falls
    // back to the roll-up banner, which names every error.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        let topmost: HTMLElement | null = null;
        let topmostY = Infinity;
        for (const k of keys) {
          const el = document.getElementById(k);
          if (!el) continue;
          const top = el.getBoundingClientRect().top;
          if (top < topmostY) {
            topmost = el;
            topmostY = top;
          }
        }
        if (topmost) {
          topmost.scrollIntoView({ behavior: "smooth", block: "center" });
          topmost.focus?.();
        } else if (errorBannerRef.current) {
          errorBannerRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }),
    );
  }

  const setBizField = patch(setBiz);
  const setOwnerField = patch(setOwner);
  const setDepositField = patch(setDeposit);
  const setWithdrawalField = patch(setWithdrawal);
  const setVolField = patch(setVol);
  const setModeField = patch(setMode);
  const updatePdi = patch(setPdiPrefs);
  const setFeeOverrideField = patch(setFeeOverrides);
  const setIcPlusPricingOverrideField = patch(setIcPlusPricingOverrides);
  const setCloverSecurityAndTransarmorField = patch(
    setCloverSecurityAndTransarmor,
  );
  const setFleetOverrideField = patch(setFleetOverrides);

  // Same volume-based tier lookup NewApplication uses to drive the "tier
  // default" placeholders in ManualFeeAdjustmentSection/
  // FleetPricingAdjustmentSection, and to gate whether those sections
  // render at all (no tier, nothing to override against).
  const tier = selectPricingTier(vol.averageMonthlyVolume);

  const posOtherMode =
    posOtherSelected || isOtherPosPlatform(pdiPrefs.posPlatform);
  const modeTotal =
    (Number(mode.eCommercePct) || 0) +
    (Number(mode.keyedPct) || 0) +
    (Number(mode.mailOrderPct) || 0) +
    (Number(mode.swipedPct) || 0);
  useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;
    if (open && !dlg.open) {
      dlg.showModal();

      setBusinessName(application.businessName ?? "");
      setMerchantEmail(application.merchantEmail ?? "");
      setTemplateID(application.templateID ?? "");
      setSalesCode(application.salesCode ?? "");
      setAssignedToPIN(application.assignedToPIN ?? "");
      setAssignedTechEmail(application.assignedTechEmail ?? "");

      const loadedBizAddr = addressFromApi(application.businessAddress);
      const loadedMailAddr = addressFromApi(application.mailingAddress);
      const loadedMailSame =
        !application.mailingAddress ||
        addressesEqual(loadedBizAddr, loadedMailAddr);
      const loadedOwnerAddr = addressFromApi(application.owner?.address);
      const loadedOwnerAddrSame =
        !application.owner?.address ||
        addressesEqual(loadedBizAddr, loadedOwnerAddr);
      const loadedDeposit = application.bankDetail?.deposit;
      const loadedWithdrawal = application.bankDetail?.withdrawal;
      const loadedWithdrawalSame =
        !loadedWithdrawal ||
        (loadedWithdrawal.accountType === loadedDeposit?.accountType &&
          loadedWithdrawal.bankName === loadedDeposit?.bankName);

      setBiz({
        businessName: application.businessName ?? "",
        dbaName: application.dbaName ?? "",
        taxFilingName: application.taxFilingName ?? "",
        taxFilingMethod: application.taxFilingMethod || "EIN",
        taxID: "",
        businessStartDate: application.businessStartDate ?? "",
        mcc: application.mcc ?? "",
        storeType: application.storeType ?? "",
        businessDescription: application.businessDescription ?? "",
        website: application.website ?? "",
        contactEmail: application.merchantEmail ?? "",
        contactPhone: application.contactPhone ?? "",
      });
      setBizAddr(loadedBizAddr);
      setMailAddr(loadedMailSame ? EMPTY_ADDRESS : loadedMailAddr);
      setMailSame(loadedMailSame);
      setVol({
        averageMonthlyVolume:
          application.averageMonthlyVolume != null
            ? String(application.averageMonthlyVolume)
            : "",
        highTicketAmount:
          application.highTicketAmount != null
            ? String(application.highTicketAmount)
            : "",
        averageTicketAmount:
          application.averageTicketAmount != null
            ? String(application.averageTicketAmount)
            : "",
      });
      setOwner({
        firstName: application.owner?.firstName ?? "",
        lastName: application.owner?.lastName ?? "",
        title: application.owner?.title || "OWNER",
        email: application.owner?.email ?? "",
        phone: application.owner?.phone ?? "",
        ssn: "",
        dateOfBirth: application.owner?.dateOfBirth ?? "",
        ownershipPct:
          application.owner?.ownershipPct != null
            ? String(application.owner.ownershipPct)
            : "100",
        ownershipTypeCd: application.owner?.ownershipTypeCd || "PUBCORP",
        driversLicenseNumber: "",
        driversLicenseStateCd: application.owner?.driversLicenseStateCd ?? "",
      });
      setOwnerAddr(loadedOwnerAddrSame ? EMPTY_ADDRESS : loadedOwnerAddr);
      setOwnerAddrSame(loadedOwnerAddrSame);
      setDeposit({
        accountNumber: "",
        confirmAccountNumber: "",
        routingNumber: "",
        accountType: loadedDeposit?.accountType || "BIZ",
        bankName: loadedDeposit?.bankName ?? "",
      });
      setWithdrawal({
        accountNumber: "",
        confirmAccountNumber: "",
        routingNumber: "",
        accountType: loadedWithdrawal?.accountType || "BIZ",
        bankName: loadedWithdrawal?.bankName ?? "",
      });
      setWithdrawalSame(loadedWithdrawalSame);
      setMode({
        eCommercePct:
          application.modeOfTransaction?.eCommercePct != null
            ? String(application.modeOfTransaction.eCommercePct)
            : "0",
        keyedPct:
          application.modeOfTransaction?.keyedPct != null
            ? String(application.modeOfTransaction.keyedPct)
            : "0",
        mailOrderPct:
          application.modeOfTransaction?.mailOrderPct != null
            ? String(application.modeOfTransaction.mailOrderPct)
            : "0",
        swipedPct:
          application.modeOfTransaction?.swipedPct != null
            ? String(application.modeOfTransaction.swipedPct)
            : "100",
      });
      setPdiPrefs({ ...EMPTY_PDI, ...(application.pdiPrefs ?? {}) });
      setMnspForm(
        fromServerMnsp(
          application.mnspEnrollment as Record<string, unknown> | null,
        ),
      );
      setAuthFeeOverride(
        application.authFeeOverride != null
          ? String(application.authFeeOverride)
          : "",
      );
      setAnnualSecurityBundleFee(
        application.annualSecurityBundleFee != null
          ? String(application.annualSecurityBundleFee)
          : "",
      );
      setFeeOverrides(feeOverridesToForm(application.feeOverrides));
      setIcPlusPricingOverrides(
        icPlusPricingOverridesToForm(application.icPlusPricingOverrides),
      );
      setCloverSecurityAndTransarmor(
        cloverSecurityAndTransarmorToForm(
          application.cloverSecurityAndTransarmor,
        ),
      );
      setFleetOverrides(fleetOverridesToForm(application.fleetOverrides));
      setInternalNotes(application.internalNotes ?? "");

      // Auto-expand: when this row never reached CardConnect and has no
      // raw identity data staged for it, the sensitive fields below aren't
      // optional this save -- see the needsIdentityReentry banner below.
      setDetailsOpen(Boolean(application.needsIdentityReentry));
      setFeeAdjustmentOpen(hasAnyOverrides(application));
      setPosOtherSelected(false);
      setGasOtherSelected(false);
      setLastAction("NONE");
      resetErrorDisplay();
      setBannerError(null);
    } else if (!open && dlg.open) {
      dlg.close();
    }
    // Only re-run on open/close — reopening always re-syncs from the
    // latest `application` prop at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;
    const handler = (e: Event) => {
      e.preventDefault();
      if (!saving) onClose();
    };
    dlg.addEventListener("cancel", handler);
    return () => dlg.removeEventListener("cancel", handler);
  }, [onClose, saving]);

  function handleBackdropPointerDown(e: React.MouseEvent<HTMLDialogElement>) {
    if (saving) return;
    if (e.target === e.currentTarget) onClose();
  }

  function validate(action: CardConnectEditAction = "NONE"): Errors {
    return validateEditApplication(
      {
        businessName,
        merchantEmail,
        templateID,
        assignedToPIN,
        assignedTechEmail,
        applicationPath: application.applicationPath ?? "",
        needsIdentityReentry: Boolean(application.needsIdentityReentry),
        owner,
        bizAddr,
        mailAddr,
        mailSame,
        ownerAddr,
        ownerAddrSame,
        taxID: biz.taxID,
        deposit,
        withdrawal,
        withdrawalSame,
        authFeeOverride,
        annualSecurityBundleFee,
        feeOverrides,
        icPlusPricingOverrides,
        cloverSecurityAndTransarmor,
        fleetOverrides,
        mnspForm,
        mnspEnrollmentRequested: !!application.mnspEnrollmentRequested,
        terminalRentalRequested: !!application.terminalRentalRequested,
        stateCd: bizAddr.stateCd,
        monthlyVolume: vol.averageMonthlyVolume,
      },
      action,
    );
  }

  // Recomputed every render so a message tracks the field as it's edited.
  // useFieldErrors gates which of these are on screen (blurred, or a Save
  // attempted) and keeps a server rejection on top until the next Save.
  const clientErrors = errorsToList(validate(lastAction));
  const {
    fieldError,
    visibleErrors,
    onBlurCapture,
    beginSubmit,
    setServerErrors,
    resetErrorDisplay,
  } = useFieldErrors(clientErrors);

  // The form's onSubmit ("Save changes"). The two CardConnect buttons call
  // doSave directly with their action.
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    void doSave("NONE");
  }

  async function doSave(action: CardConnectEditAction) {
    if (saving) return;
    // Validate for the button actually pressed, not whatever was pressed
    // last, and make that the live rule set from here on.
    setLastAction(action);
    beginSubmit();
    const nextErrors = validate(action);
    if (Object.keys(nextErrors).length > 0) {
      focusFirstError(Object.keys(nextErrors));
      return;
    }

    setSavingAction(action);
    setBannerError(null);
    try {
      const input: UpdateApplicationInput = {
        businessName: businessName.trim(),
        merchantEmail: merchantEmail.trim(),
        templateID: templateID.trim(),
        // Always sent, authoritative (like the pricing overrides below):
        // "" = Auto clears any stored override. Only this value reaches the
        // CardConnect merchant update; the paired templateID above is
        // persisted to our DB but never re-boarded.
        salesCode,
        storeType: biz.storeType || undefined,
        assignedToPIN: assignedToPIN || undefined,
        assignedTechEmail: assignedTechEmail || undefined,
        averageMonthlyVolume: vol.averageMonthlyVolume
          ? Number(vol.averageMonthlyVolume)
          : undefined,
        highTicketAmount: vol.highTicketAmount
          ? Number(vol.highTicketAmount)
          : undefined,
        averageTicketAmount: vol.averageTicketAmount
          ? Number(vol.averageTicketAmount)
          : undefined,
        // The PDI section is edited on every path now -- on MNSP-enrollment-
        // only the managed-network order is opt-in via pdiPrefs.sendPdiOrder.
        pdiPrefs:
          application.mnspEnrollmentRequested ||
          application.terminalRentalRequested
            ? {
                ...pdiPrefs,
                // Shipping speed is sourced from the MNSP selection (single
                // source of truth), mirroring NewApplication's own
                // buildInput() -- the PDI section has no dropdown for it.
                // Only overridden when MNSP/rental is actually part of this
                // save; the server preserves the stored value otherwise.
                shippingSpeed: mnspShippingToPdiLabel(mnspForm.shippingSpeed),
              }
            : pdiPrefs,
        mnspEnrollment:
          application.mnspEnrollmentRequested ||
          application.terminalRentalRequested
            ? toUpdateMnspInput(
                mnspForm,
                !!application.mnspEnrollmentRequested,
                !!application.terminalRentalRequested,
              )
            : undefined,
        // Admin-only pricing overrides. ALWAYS sent (never omitted) and
        // authoritative -- unlike most fields on this input, blank here
        // genuinely means "no override," so this is the one place in the
        // modal that can clear a previously-set value back to pure
        // tiered pricing, not just change it.
        authFeeOverride: parseAuthFeeOverride(authFeeOverride) ?? null,
        // Annual security bundle fee -- always authoritative like the rest;
        // null clears it so display falls back to the $199 tier default.
        // Never sent to CardConnect. An invalid value (blocked by validation
        // before Save) also collapses to null rather than sending NaN.
        annualSecurityBundleFee: annualSecurityBundleFeeInvalid(
          annualSecurityBundleFee,
        )
          ? null
          : (parseAnnualSecurityBundleFee(annualSecurityBundleFee) ?? null),
        feeOverrides: buildFeeOverridesInput(feeOverrides) ?? {},
        icPlusPricingOverrides:
          buildIcPlusPricingOverridesInput(icPlusPricingOverrides) ?? {},
        cloverSecurityAndTransarmor:
          buildCloverSecurityAndTransarmorInput(cloverSecurityAndTransarmor) ??
          {},
        fleetOverrides: buildFleetOverridesInput(fleetOverrides) ?? {},
        // Operator-only free-text notes. Same always-authoritative posture
        // as the pricing overrides above -- blank clears a previously-set
        // note. Never surfaced to the merchant or CardConnect.
        internalNotes: internalNotes.trim(),
        dbaName: biz.dbaName || undefined,
        taxFilingName: biz.taxFilingName || undefined,
        taxFilingMethod: biz.taxFilingMethod || undefined,
        // Write-only: taxID always renders blank (redacted to a masked
        // last-4 at write time, see the header comment above), so
        // "undefined" here means "admin didn't type a new one," not
        // "clear it" -- the backend leaves the stored/CardConnect value
        // untouched in that case.
        taxID: biz.taxID || undefined,
        businessDescription: biz.businessDescription || undefined,
        website: biz.website || undefined,
        businessStartDate: biz.businessStartDate || undefined,
        contactPhone: biz.contactPhone || undefined,
        mcc: biz.mcc || undefined,
        businessAddress: bizAddr,
        mailingAddress: mailSame ? bizAddr : mailAddr,
        owner: {
          firstName: owner.firstName || undefined,
          lastName: owner.lastName || undefined,
          title: owner.title || undefined,
          email: owner.email || undefined,
          phone: owner.phone || undefined,
          ownershipTypeCd: owner.ownershipTypeCd || undefined,
          ownershipPct: owner.ownershipPct
            ? Number(owner.ownershipPct)
            : undefined,
          dateOfBirth: owner.dateOfBirth || undefined,
          // Write-only, same "blank = don't change" posture as taxID above.
          ssn: owner.ssn || undefined,
          driversLicenseNumber: owner.driversLicenseNumber || undefined,
          driversLicenseStateCd: owner.driversLicenseStateCd || undefined,
          address: ownerAddrSame ? bizAddr : ownerAddr,
        },
        bankDetail: {
          deposit: {
            accountType: deposit.accountType || undefined,
            bankName: deposit.bankName || undefined,
            // Write-only, same "blank = don't change" posture as taxID above.
            accountNumber: deposit.accountNumber || undefined,
            routingNumber: deposit.routingNumber || undefined,
          },
          withdrawal: withdrawalSame
            ? {
                accountType: deposit.accountType || undefined,
                bankName: deposit.bankName || undefined,
                accountNumber: deposit.accountNumber || undefined,
                routingNumber: deposit.routingNumber || undefined,
              }
            : {
                accountType: withdrawal.accountType || undefined,
                bankName: withdrawal.bankName || undefined,
                accountNumber: withdrawal.accountNumber || undefined,
                routingNumber: withdrawal.routingNumber || undefined,
              },
        },
        modeOfTransaction: {
          eCommercePct: Number(mode.eCommercePct) || 0,
          keyedPct: Number(mode.keyedPct) || 0,
          mailOrderPct: Number(mode.mailOrderPct) || 0,
          swipedPct: Number(mode.swipedPct) || 0,
        },
      };

      const data = await gql<{ updateApplication: ApplicationResult }>(
        UPDATE_APPLICATION_MUTATION,
        {
          applicationID: application.applicationID,
          input,
          cardConnectAction: action === "NONE" ? undefined : action,
        },
      );
      const r = data.updateApplication;
      if (r.status === "success") {
        // A success envelope that still carries an errorCode is a soft
        // warning (the save landed, but e.g. the signature link failed) --
        // the parent renders it red.
        onSaved(r.message ?? null, r.errorCode ? "warning" : "success");
      } else {
        setBannerError(
          `${r.message ?? "Update failed."}${
            r.errorCode ? ` (${r.errorCode})` : ""
          }`,
        );
        if (r.fieldErrors?.length) {
          setServerErrors(
            r.fieldErrors.map((fe) => ({
              field: fe.field,
              message: fe.message,
            })),
          );
          focusFirstError(r.fieldErrors.map((fe) => fe.field));
        }
      }
    } catch (err: unknown) {
      setBannerError(
        err instanceof GraphQLError ? err.message : "Update failed.",
      );
    } finally {
      setSavingAction(null);
    }
  }

  // Which CardConnect action buttons apply to this row. A row has a
  // CardConnect merchant or it doesn't, so at most one shows.
  const hasMerchant = Boolean(application.cardConnectMerchantID);
  // RESEND_SIGNATURE: push the edit to the live merchant + email a fresh
  // signature link. Only meaningful once a merchant exists on CardConnect;
  // the backend re-checks the live boarding status and rejects it once the
  // merchant is past OFS.
  const canResend =
    hasMerchant &&
    ![
      "BOARDED",
      "ABANDONED",
      "ARCHIVED",
      "IN_REVIEW",
      "APPROVED",
      "REJECTED",
      "SUBMITTED",
    ].includes(application.status);
  // A signature link only exists once request_signature actually returned a
  // URL. A merchant whose signature step failed (ERROR + INPROG) has a
  // merchant but no link -- so this is a first send, not a resend.
  const ccSignatureLabel = application.signatureUrl
    ? "Save & resend CardConnect signature"
    : "Save & send CardConnect signature";
  // SUBMIT: full create + signature for a row with no merchant at all --
  // an ERROR row that never reached CardConnect, or a pre-signature
  // send-intake row.
  const canSubmit =
    !hasMerchant &&
    application.applicationPath !== "MNSP_ENROLLMENT_ONLY" &&
    [
      "ERROR",
      "LINK_SENT",
      "LINK_OPENED",
      "FORM_IN_PROGRESS",
      "READY_FOR_REVIEW",
    ].includes(application.status);

  // Issue 4: this row is still editable (its CardConnect MPA is unfinished),
  // but the merchant has already signed the MAR. Saving any change voids that
  // signature server-side and requires a re-sign, so warn before the operator
  // saves. Only the bundled path can be in this state -- a signed enrollment-
  // only row is MNSP_SIGNED and read-only.
  const marAlreadySigned = !!(
    application.mnspEnrollment as { signatureImage?: string } | null
  )?.signatureImage;
  const willInvalidateMar =
    marAlreadySigned &&
    (!!application.mnspEnrollmentRequested ||
      !!application.terminalRentalRequested);

  return (
    <dialog
      ref={dialogRef}
      onMouseDown={(e) => handleBackdropPointerDown(e)}
      aria-labelledby="edit-application-title"
      className="m-auto w-full max-w-3xl rounded-xl border border-brand-border bg-brand-surface p-0 text-brand-text shadow-pop backdrop:bg-black/60 backdrop:backdrop-blur-sm"
    >
      <form
        onSubmit={handleSubmit}
        onBlurCapture={onBlurCapture}
        autoComplete="off"
        noValidate
      >
        <div className="flex items-center justify-between border-b border-brand-border px-6 py-4">
          <h2
            id="edit-application-title"
            className="text-lg font-semibold tracking-tight text-white"
          >
            Edit application
          </h2>
          <button
            type="button"
            onClick={() => !saving && onClose()}
            className="rounded p-1 text-brand-muted hover:bg-brand-bg hover:text-brand-text"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-[70vh] space-y-5 overflow-y-auto px-6 py-5">
          {application.applicationPath === "MNSP_ENROLLMENT_ONLY" && (
            <p className="callout-info px-3.5 py-2.5">
              MNSP Enrollment Only — this application is never submitted to
              CardConnect. The application path cannot be changed after
              creation.
            </p>
          )}
          {application.needsIdentityReentry && (
            <p className="rounded-lg border border-warning/30 bg-warning/10 px-3.5 py-2.5 text-sm text-amber-200">
              This application never reached CardConnect.{" "}
              <span className="font-medium">Save changes</span> saves your edits
              without retrying. To retry, enter the owner's SSN, tax ID and
              deposit account/routing numbers below (under "Adjust details"),
              then use{" "}
              <span className="font-medium">
                Save &amp; submit to CardConnect
              </span>
              .
            </p>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TF
              id="edit-businessName"
              label="Legal business name"
              value={businessName}
              onChange={setBusinessName}
              maxLength={500}
              required
              error={fieldError("businessName")}
            />
            <TF
              id="edit-merchantEmail"
              label="Merchant email"
              type="email"
              value={merchantEmail}
              onChange={setMerchantEmail}
              maxLength={320}
              required
              error={fieldError("merchantEmail")}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <TF
              id="edit-assignedToPIN"
              label="Assigned salesperson PIN"
              value={assignedToPIN}
              onChange={setAssignedToPIN}
              format="integer"
              maxLength={8}
              hint="4-8 digits, leave blank to clear."
              error={fieldError("assignedToPIN")}
            />
            <TF
              id="edit-assignedTechEmail"
              label="Assigned tech email"
              type="email"
              value={assignedTechEmail}
              onChange={setAssignedTechEmail}
              maxLength={320}
              error={fieldError("assignedTechEmail")}
            />
          </div>

          <div>
            <SalesCodeSelect
              id="edit-salesCode"
              includeAllPortfolios
              value={salesCode}
              onChange={(next) => {
                setSalesCode(next);
                // Re-pair the template (persisted to our DB). Auto leaves
                // whatever template the row already had.
                const paired = templateIdForSalesCode(next, {
                  dev: IS_DEV_API,
                });
                if (paired) setTemplateID(paired);
              }}
              error={fieldError("salesCode")}
            />
            <p className="hint">
              Template{" "}
              <code className="text-brand-text">{templateID || "—"}</code> is
              saved to our records. The CardConnect merchant update sends only
              the sales code, never the template.
            </p>
          </div>

          <PersistedSensitiveLast4Panel
            value={application.persistedSensitiveLast4}
          />

          {/* Optional prefill: the SAME shared components + state shapes
              NewApplication's own "Fill out some of it (optional)"
              send-intake section uses, INCLUDING the sensitive fields (SSN,
              driver's license number, tax ID, bank account/routing
              numbers) — those are redacted to a masked last-4 at write
              time, so they always render blank (never prefilled) and a
              blank submit leaves the stored value untouched; a non-blank
              value overwrites both CardConnect's live record and the
              stored last-4. Collapsed by default so the modal opens quick
              for the common case of tweaking just the fields above. */}
          <div className="panel overflow-hidden">
            <button
              type="button"
              onClick={() => setDetailsOpen((v) => !v)}
              className="flex w-full items-center justify-between px-4 py-3 text-left text-sm text-brand-text transition-colors hover:bg-brand-surface-2/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-blue-bright/40"
              aria-expanded={detailsOpen}
              aria-controls="edit-details-section"
            >
              <span className="font-semibold text-white">Adjust details</span>
              <span className="flex items-center gap-2 text-xs text-brand-muted">
                {detailsOpen ? "Hide" : "Business, ownership, banking, PDI"}
                {detailsOpen ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </span>
            </button>
            {detailsOpen && (
              <div
                id="edit-details-section"
                className="space-y-7 border-t border-brand-border px-4 py-5"
              >
                <Section title="Business details">
                  <BusinessDetailsFields
                    biz={biz}
                    setBizField={setBizField}
                    fieldError={fieldError}
                    required={false}
                  />
                  <VolumeFields
                    vol={vol}
                    setVolField={setVolField}
                    authFeeOverride={authFeeOverride}
                    feeOverrides={feeOverrides}
                    fleetOverrides={fleetOverrides}
                    cloverSecurityAndTransarmor={cloverSecurityAndTransarmor}
                    icPlusPricingOverrides={icPlusPricingOverrides}
                    salesCode={salesCode}
                    annualSecurityBundleFee={annualSecurityBundleFee}
                    fieldError={fieldError}
                  />
                </Section>
                <Section title="Business address">
                  <AddressFields
                    prefix="biz"
                    value={bizAddr}
                    set={(k, v) => setBizAddr((p) => ({ ...p, [k]: v }))}
                    errorAt={fieldError}
                    required={false}
                  />
                  <SameAsToggle
                    checked={mailSame}
                    onChange={setMailSame}
                    label="Mailing address is the same as the business address"
                    subtitle="Mailing address"
                  >
                    <AddressFields
                      prefix="mail"
                      value={mailAddr}
                      set={(k, v) => setMailAddr((p) => ({ ...p, [k]: v }))}
                      errorAt={fieldError}
                      required={false}
                    />
                  </SameAsToggle>
                </Section>
                <Section title="Ownership">
                  <OwnershipFields
                    owner={owner}
                    setOwnerField={setOwnerField}
                    ownerAddr={ownerAddr}
                    setOwnerAddr={setOwnerAddr}
                    ownerAddrSame={ownerAddrSame}
                    setOwnerAddrSame={setOwnerAddrSame}
                    fieldError={fieldError}
                    required={false}
                  />
                </Section>
                <Section title="Banking">
                  <BankingFields
                    deposit={deposit}
                    setDepositField={setDepositField}
                    withdrawal={withdrawal}
                    setWithdrawalField={setWithdrawalField}
                    withdrawalSame={withdrawalSame}
                    setWithdrawalSame={setWithdrawalSame}
                    fieldError={fieldError}
                    required={false}
                  />
                </Section>
                <Section
                  title="Transaction mix"
                  aside={
                    <PercentageTotalIndicator
                      id="edit-modeOfTransaction-total"
                      total={modeTotal}
                    />
                  }
                >
                  <TransactionMixFields
                    mode={mode}
                    setModeField={setModeField}
                    fieldError={fieldError}
                    required={false}
                  />
                </Section>
                {/* Shown on every path. On MNSP-enrollment-only the
                    managed-network order is opt-in (toggle defaults to "No"). */}
                {
                  <Section title="Equipment & network (PDI)">
                    <PdiFields
                      pdiPrefs={pdiPrefs}
                      updatePdi={updatePdi}
                      posOtherMode={posOtherMode}
                      setPosOtherSelected={setPosOtherSelected}
                      gasOtherSelected={gasOtherSelected}
                      setGasOtherSelected={setGasOtherSelected}
                      enrollmentOnly={
                        application.applicationPath === "MNSP_ENROLLMENT_ONLY"
                      }
                    />
                  </Section>
                }
                {/* Equipment/service fields only -- noSignature hides the
                    acceptance checkboxes + signature pad, matching
                    NewApplication's own operator-side usage. Only shown
                    when this application actually has an MNSP enrollment
                    and/or terminal rental; nothing to edit otherwise.
                    Which of mnspEnabled/rentalEnabled is on is fixed at
                    creation time (not editable here), mirroring the
                    disabled-checkbox pattern in NewApplication. */}
                {(application.mnspEnrollmentRequested ||
                  application.terminalRentalRequested) && (
                  <MnspEnrollmentSection
                    form={mnspForm}
                    onChange={setMnspForm}
                    mnspEnabled={!!application.mnspEnrollmentRequested}
                    rentalEnabled={!!application.terminalRentalRequested}
                    feeError={fieldError}
                    noSignature
                  />
                )}
              </div>
            )}
          </div>
          {/* Nested one level deeper than "Fill out more details" --
                    admin-only pricing overrides, mirroring NewApplication's
                    Manual fee adjustment + Fleet pricing adjustment
                    sections verbatim (same components, same tier-default
                    placeholders). Collapsed by default; only worth opening
                    for the rare special-pricing case. */}
          <div className="panel overflow-hidden">
            <button
              type="button"
              onClick={() => setFeeAdjustmentOpen((v) => !v)}
              className="flex w-full items-center justify-between px-4 py-3 text-left text-sm text-brand-text transition-colors hover:bg-brand-surface-2/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-blue-bright/40"
              aria-expanded={feeAdjustmentOpen}
              aria-controls="edit-fee-adjustment-section"
            >
              <span className="font-semibold text-white">
                Manual fee adjustment{" "}
                <span className="font-normal text-brand-muted">(optional)</span>
              </span>
              <span className="flex items-center gap-2 text-xs text-brand-muted">
                {feeAdjustmentOpen
                  ? "Hide"
                  : "Fees, interchange-plus pricing, Clover Security + TransArmor, fleet pricing"}
                {feeAdjustmentOpen ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </span>
            </button>
            {feeAdjustmentOpen && (
              <div
                id="edit-fee-adjustment-section"
                className="space-y-5 border-t border-brand-border px-4 py-5"
              >
                <ManualFeeAdjustmentSection
                  idPrefix="fee-override"
                  tier={tier}
                  monthlyVolume={vol.averageMonthlyVolume}
                  salesCode={salesCode}
                  authFeeOverride={authFeeOverride}
                  onAuthFeeOverrideChange={setAuthFeeOverride}
                  authFeeOverrideError={fieldError("fee-override.authFee")}
                  annualSecurityBundleFee={annualSecurityBundleFee}
                  onAnnualSecurityBundleFeeChange={setAnnualSecurityBundleFee}
                  annualSecurityBundleFeeError={fieldError(
                    "fee-override.annualSecurityBundleFee",
                  )}
                  feeOverrides={feeOverrides}
                  onFeeOverrideChange={setFeeOverrideField}
                  errorAt={fieldError}
                  icPlusPricingOverrides={icPlusPricingOverrides}
                  onIcPlusPricingOverrideChange={setIcPlusPricingOverrideField}
                />
                <CloverSecurityAndTransarmorSection
                  idPrefix="clover-security"
                  tier={tier}
                  cloverSecurityAndTransarmor={cloverSecurityAndTransarmor}
                  onCloverSecurityAndTransarmorChange={
                    setCloverSecurityAndTransarmorField
                  }
                  feeOverrides={feeOverrides}
                  onFeeOverrideChange={setFeeOverrideField}
                  feeIdPrefix="fee-override"
                  errorAt={fieldError}
                />
                <FleetPricingAdjustmentSection
                  idPrefix="fleet-override"
                  tier={tier}
                  fleetOverrides={fleetOverrides}
                  onFleetOverrideChange={setFleetOverrideField}
                  errorAt={fieldError}
                />
              </div>
            )}
          </div>

          <div>
            <label htmlFor="edit-internal-notes" className="label mb-1.5 block">
              Internal notes
            </label>
            <textarea
              id="edit-internal-notes"
              className="input min-h-[88px] resize-y"
              aria-describedby="edit-internal-notes-hint"
              value={internalNotes}
              onChange={(e) => setInternalNotes(e.target.value)}
              maxLength={2000}
              placeholder="e.g. MNSP reduced to $115/mo, deployment fee waived"
            />
            <p id="edit-internal-notes-hint" className="hint">
              Admin only — pricing context, special arrangements, etc. Never
              shown to the merchant.
            </p>
          </div>

          {visibleErrors.length > 0 && (
            <div
              ref={errorBannerRef}
              role="alert"
              aria-live="assertive"
              className="callout-danger flex gap-3 px-4 py-3"
            >
              <AlertCircle
                className="mt-0.5 h-4 w-4 shrink-0 text-danger"
                aria-hidden="true"
              />
              <div className="min-w-0 flex-1">
                <p className="font-medium text-white">
                  {visibleErrors.length}{" "}
                  {visibleErrors.length === 1 ? "field needs" : "fields need"}{" "}
                  attention before saving
                </p>
                <ul className="mt-1.5 space-y-0.5">
                  {visibleErrors.map(({ field, message }) => (
                    <li key={field}>
                      <button
                        type="button"
                        onClick={() => focusFirstError([field])}
                        className="text-left text-danger-soft underline-offset-2 hover:text-white hover:underline focus:outline-none focus:underline"
                      >
                        {message}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {bannerError && <ErrorMessage message={bannerError} />}
        </div>

        <div className="flex flex-col gap-2 border-t border-brand-border bg-brand-bg/40 px-6 py-3">
          {willInvalidateMar && (
            <p className="text-right text-xs font-medium text-amber-600 dark:text-amber-500">
              This application’s MAR is already signed. Saving any change will
              invalidate that signature and require the merchant to re-sign.{" "}
              {canResend || canSubmit
                ? "Use a “Save & …” action to email them the enrollment link."
                : "Then use “Save & resend CardConnect signature” to email them the enrollment link."}
            </p>
          )}
          {(canResend || canSubmit) && (
            <p className="text-right text-xs text-brand-muted">
              “Save changes” only updates our records.{" "}
              {canResend
                ? `“${ccSignatureLabel.replace("Save & ", "")}” also pushes the change to CardConnect and emails the merchant a signature link.`
                : "“Save & submit…” sends it to CardConnect and emails the merchant a signature link."}
            </p>
          )}
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => !saving && onClose()}
              disabled={saving}
              className="btn-secondary py-2"
            >
              Cancel
            </button>
            {canResend && (
              <button
                type="button"
                onClick={() => void doSave("RESEND_SIGNATURE")}
                disabled={saving}
                className="btn-secondary py-2"
              >
                {savingAction === "RESEND_SIGNATURE"
                  ? "Saving…"
                  : ccSignatureLabel}
              </button>
            )}
            {canSubmit && (
              <button
                type="button"
                onClick={() => void doSave("SUBMIT")}
                disabled={saving}
                className="btn-secondary py-2"
              >
                {savingAction === "SUBMIT"
                  ? "Submitting…"
                  : "Save & submit to CardConnect"}
              </button>
            )}
            <button
              type="submit"
              disabled={saving}
              className="btn-primary py-2"
            >
              {savingAction === "NONE" ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </form>
    </dialog>
  );
}
